import React, { useState, useCallback, useId } from 'react';
import cx from 'classnames';

import ChevronDownIcon from '@/storybook/icons/ChevronDownIcon';
import Text from '@/storybook/components/Text/Text';

import styles from './Accordion.module.css';

type ChevronPosition = 'start' | 'end';
type Size = 'L' | 'S';
type DividerVariant = 'default' | 'topWhenCollapsed';

export type AccordionProps = {
  'title'?: React.ReactNode;
  'meta'?: React.ReactNode;
  'actions'?: React.ReactNode;
  'size': Size;
  'dividerVariant'?: DividerVariant;
  'chevronPosition'?: ChevronPosition;
  'defaultExpanded'?: boolean;
  'expanded'?: boolean;
  'onExpandedChange'?: (expanded: boolean) => void;
  'description'?: string;
  'children'?: React.ReactNode;
  'disabled'?: boolean;
  /** When true, disables the hover/focus border on accordion body. */
  'disableBodyHoverBorder'?: boolean;
  'className'?: string;
  'contentClassName'?: string;
  'data-testid'?: string;
};

type ChevronProps = {
  open: boolean;
  disabled: boolean;
  testId: string;
};

const AccordionChevron: React.FC<ChevronProps> = ({ open, disabled, testId }) => (
  <ChevronDownIcon
    className={cx(styles.accordionChevron, {
      [styles.chevronOpen]: open,
      [styles.chevronDisabled]: disabled,
    })}
    aria-hidden="true"
    data-testid={`${testId}-chevron`}
  />
);

const Accordion: React.FC<AccordionProps> = ({
  title,
  meta,
  actions,
  size = 'L',
  dividerVariant = 'default',
  defaultExpanded = false,
  expanded,
  onExpandedChange,
  description,
  children,
  disabled = false,
  disableBodyHoverBorder = false,
  chevronPosition = 'end',
  className,
  contentClassName,
  'data-testid': dataTestId = 'accordion',
}) => {
  const contentId = useId();
  const [expandedState, setExpandedState] = useState<boolean>(defaultExpanded);

  const isControlled = typeof expanded === 'boolean';
  const isExpanded = expanded ?? expandedState;

  const toggle = useCallback(() => {
    if (disabled) return;

    const nextExpanded = !isExpanded;
    if (!isControlled) {
      setExpandedState(nextExpanded);
    }

    onExpandedChange?.(nextExpanded);
  }, [disabled, isExpanded, isControlled, onExpandedChange]);

  const handleTriggerKeyDown = useCallback(
    (keyboardEvent: React.KeyboardEvent) => {
      if (keyboardEvent.key !== 'Enter' && keyboardEvent.key !== ' ') return;
      keyboardEvent.preventDefault();
      toggle();
    },
    [toggle],
  );

  const chevronIcon = <AccordionChevron open={isExpanded} disabled={disabled} testId={dataTestId} />;

  return (
    <div
      className={cx(styles.accordion, className)}
      data-size={size}
      data-divider-variant={dividerVariant}
      data-disabled={disabled}
      data-expanded={isExpanded}
      data-disable-body-hover-border={disableBodyHoverBorder || undefined}
      data-testid={dataTestId}
    >
      {dividerVariant === 'topWhenCollapsed' && (
        <div className={styles.accordionTopDivider} data-testid={`${dataTestId}-top-divider`} />
      )}
      <div
        className={styles.accordionTrigger}
        tabIndex={disabled ? -1 : 0}
        aria-expanded={isExpanded}
        aria-controls={contentId}
        aria-disabled={disabled}
        data-disabled={disabled}
        onClick={toggle}
        onKeyDown={handleTriggerKeyDown}
        data-testid={`${dataTestId}-trigger`}
      >
        <div className={styles.accordionHeaderInfo}>
          {chevronPosition === 'start' && chevronIcon}
          {title && (
            <Text className={styles.accordionTitle} data-testid={`${dataTestId}-title`}>
              {title}
            </Text>
          )}
          {meta && <div className={styles.accordionMeta}>{meta}</div>}
        </div>

        <div className={styles.accordionHeaderActions}>
          {actions && actions}
          {chevronPosition === 'end' && chevronIcon}
        </div>
      </div>
      <div
        id={contentId}
        className={styles.accordionBody}
        data-expanded={isExpanded}
        tabIndex={isExpanded ? 0 : -1}
        data-testid={`${dataTestId}-body`}
      >
        <div className={styles.accordionContentWrapper}>
          <div className={cx(styles.accordionContentInner, contentClassName)}>
            {description && (
              <Text as="p" className={styles.accordionDescription}>
                {description}
              </Text>
            )}
            <div className={styles.accordionContent} data-testid={`${dataTestId}-content`}>
              {children}
            </div>
          </div>
        </div>
        <div className={styles.accordionDivider} data-testid={`${dataTestId}-divider`} />
      </div>
    </div>
  );
};

export default Accordion;
